/**
 * Mijn week — /overleg. Startpagina per persoon: overleggen deze week (met invulblok-status), mijn open
 * acties uit alle overleggen (te laat bovenaan), cijfers die ik nog moet invullen, recente recaps.
 * Wat je hier ziet is per overleg gefilterd: alleen overleggen waar je deelnemer van bent.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import PageHeader from '../../ui-components/PageHeader';
import { overlegApi } from './overlegApi';
import { SessieStatusChip, TodoLijst, datumKort, tijdKort } from './OverlegDelen';
import { NietGehouden, VerzetMenuKnop, useVerzetten } from './Verzetten';

export default function MijnWeek() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  const [waarden, setWaarden] = useState({});

  const laad = useCallback(async () => {
    try { setData(await overlegApi.mijnWeek(false)); setFout(''); } catch (e) { setFout(e.message || 'Laden mislukt'); }
  }, []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);
  const { verzet } = useVerzetten(laad);

  const toggleTodo = async (todo) => {
    const nieuw = todo.status === 'gedaan' ? 'open' : 'gedaan';
    try { await overlegApi.todoPatch(todo.id, { status: nieuw }); } catch (e) { toast.error(e.message); return; }
    laad();
    biedOngedaanAan(nieuw === 'gedaan' ? 'Actie afgevinkt' : 'Actie heropend', async () => { await overlegApi.todoPatch(todo.id, { status: todo.status }); laad(); }, { eigenKnop: true });
  };
  const vulIn = async (m) => {
    const w = waarden[m.measurable_id];
    if (w === undefined || w === '') return;
    try { await overlegApi.waardePut(m.measurable_id, m.periode, Number(w)); toast.success(`${m.naam} ingevuld`); laad(); } catch (e) { toast.error(e.message); }
  };

  if (!data && !fout) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  const dezeWeek = (data?.sessies || []).filter((s) => s.deze_week);
  const volgende = (data?.sessies || []).filter((s) => !s.deze_week);

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="event_note" title="Mijn week" subtitle="Je overleggen, acties en cijfers: één plek, vooraf gevuld"
        accent={{ bg: '#e0e7ff', color: '#4338ca' }}
        rightActions={<Button variant="outlined" size="small" onClick={() => navigate('/overleg/acties')} sx={{ textTransform: 'none', fontWeight: 700 }}>Actieregister</Button>} />
      {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}
      {data && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 3 }}>
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1.5 }}>📅 Overleggen deze week</Typography>
              <NietGehouden items={data.niet_gehouden} onVeranderd={laad} />
              {(data.hangend || []).map((s) => (
                <Typography key={s.id} onClick={() => navigate(`/overleg/sessie/${s.id}`)}
                  sx={{ fontSize: 13, color: '#64748b', mb: 1, cursor: 'pointer', '&:hover': { color: '#334155', textDecoration: 'underline' } }}>
                  {s.type_naam} van {datumKort(s.datum)} is nooit afgesloten — open om af te sluiten
                </Typography>
              ))}
              <Stack spacing={1}>
                {dezeWeek.map((s) => (
                  <Paper key={s.id} variant="outlined" onClick={() => navigate(`/overleg/sessie/${s.id}`)} sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }, borderLeft: `4px solid ${s.status === 'bezig' ? '#16a34a' : '#4338ca'}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{s.type_naam}</Typography>
                      <SessieStatusChip status={s.status} />
                      {s.vertrouwelijk && <Chip size="small" label="vertrouwelijk" sx={{ height: 20, fontSize: 11, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />}
                      <Box sx={{ flex: 1 }} />
                      <Typography sx={{ fontSize: 13, color: '#64748b' }}>{datumKort(s.datum)} · {tijdKort(s.gepland_start_nl)} · {s.mijn_rol}</Typography>
                      <VerzetMenuKnop sessie={s} onVerzet={verzet} />
                    </Stack>
                    {s.prep_nodig && (
                      <Typography sx={{ fontSize: 12.5, mt: 0.5, color: s.prep_ingediend ? '#166534' : '#9a3412', fontWeight: 600 }}>
                        {s.prep_ingediend ? '✓ Invulblok ingediend' : '○ Invulblok nog niet ingevuld, twee minuten'}
                      </Typography>
                    )}
                  </Paper>
                ))}
                {dezeWeek.length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen overleggen deze week{data.types?.length ? '' : ', je bent nog geen deelnemer van een overleg'}.</Typography>}
              </Stack>
              {volgende.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mb: 0.5 }}>Volgende week</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {volgende.map((s) => (
                      <Box key={s.id} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Chip clickable onClick={() => navigate(`/overleg/sessie/${s.id}`)} label={`${s.type_naam} · ${datumKort(s.datum)}`} sx={{ fontWeight: 600 }} />
                        <VerzetMenuKnop sessie={s} onVerzet={verzet} sx={{ ml: 0.25 }} />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>
            {(data.sessies || []).some((s) => s.prep_nodig && s.ik_doe_mee) && (
              <Paper variant="outlined" sx={{ p: 2.5, borderColor: '#c7d2fe', bgcolor: '#fafaff' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}>📝 Jouw voorbereiding</Typography>
                <Stack spacing={0.75}>
                  {(data.sessies || []).filter((s) => s.prep_nodig && s.ik_doe_mee).map((s) => (
                    <Stack key={s.id} direction="row" spacing={1} alignItems="center" onClick={() => navigate(`/overleg/sessie/${s.id}`)} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.85 } }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.prep_ingediend ? '#16a34a' : '#f59e0b', flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{s.type_naam} · {datumKort(s.datum)}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: s.prep_ingediend ? '#166534' : (Date.parse(s.prep_deadline) < Date.now() ? '#991b1b' : '#9a3412'), fontWeight: 600 }}>
                        {s.prep_ingediend ? 'ingediend' : (Date.parse(s.prep_deadline) < Date.now() ? `nog niet ingevuld, deadline was ${datumKort(s.prep_deadline)} ${tijdKort(s.prep_deadline)}` : `vul vóór ${datumKort(s.prep_deadline)} ${tijdKort(s.prep_deadline)} in`)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15 }}>✅ Mijn open acties</Typography>
                {data.te_laat > 0 && <Chip size="small" label={`${data.te_laat} te laat`} sx={{ height: 20, fontWeight: 800, bgcolor: '#fee2e2', color: '#991b1b' }} />}
              </Stack>
              <TodoLijst todos={data.todos} onToggle={toggleTodo} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Acties uit een overleg waar je geen deelnemer van bent, zie je alleen als losse regel.</Typography>
            </Paper>
          </Stack>
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2.5, borderColor: data.in_te_vullen?.length ? '#fcd34d' : '#e2e8f0', bgcolor: data.in_te_vullen?.length ? '#fffbeb' : '#fff' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 0.25 }}>🔢 Cijfers in te vullen</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Opgeslagen zodra je het veld verlaat.</Typography>
              <Stack spacing={1}>
                {(data.in_te_vullen || []).map((m, i, arr) => (
                  <React.Fragment key={m.measurable_id}>
                  {(i === 0 || arr[i - 1].periode !== m.periode) && (
                    <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', pt: i ? 1 : 0 }}>
                      {m.ritme === 'week' ? 'Week' : (m.ritme === 'maand' ? 'Maand' : 'Kwartaal')} {m.periode}{m.voor_overleg ? ` · voor ${m.voor_overleg}` : ''}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{m.naam}</Typography><Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{m.doel_tekst ? `norm: ${m.doel_tekst}` : 'volgen'}</Typography></Box>
                    <TextField size="small" type="number" placeholder={m.eenheid === 'pct' ? '%' : (m.eenheid === 'eur' ? '€' : m.eenheid)} value={waarden[m.measurable_id] ?? ''} onChange={(e) => setWaarden((p) => ({ ...p, [m.measurable_id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} onBlur={() => vulIn(m)} sx={{ width: 120 }} inputProps={{ step: 'any' }} />
                  </Stack>
                  </React.Fragment>
                ))}
                {(data.in_te_vullen || []).length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Alles ingevuld voor deze periode.</Typography>}
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}>🧾 Recente recaps</Typography>
              <Stack spacing={0.5}>
                {(data.recaps || []).map((s) => (
                  <Stack key={s.id} direction="row" spacing={1} alignItems="center" onClick={() => navigate(`/overleg/sessie/${s.id}`)} sx={{ cursor: 'pointer', py: 0.5, '&:hover': { opacity: 0.8 } }}>
                    <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{s.type_naam} · {datumKort(s.datum)}</Typography>
                    {s.rating_gem != null && <Chip size="small" label={`★ ${s.rating_gem}`} sx={{ height: 20, fontWeight: 700 }} />}
                  </Stack>
                ))}
                {(data.recaps || []).length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Nog geen afgesloten overleggen.</Typography>}
              </Stack>
            </Paper>
            {data.rocks_mijn?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}>🪨 Mijn Rocks</Typography>
                <Stack spacing={0.5}>
                  {data.rocks_mijn.map((r) => (
                    <Stack key={r.id} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.stoplicht === 'groen' ? '#16a34a' : (r.stoplicht === 'oranje' ? '#f59e0b' : '#dc2626'), flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{r.titel}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>{r.kwartaal || ''}{r.verlopen ? ' · verlopen' : ''} · {r.progress_pct}%</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button size="small" href="/strategy" sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>Bijwerken op Strategie →</Button>
              </Paper>
            )}
            {data.issues_mijn?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}>❓ Issues waar ik eigenaar van ben</Typography>
                <Stack spacing={0.5}>{data.issues_mijn.map((i) => <Typography key={i.id} sx={{ fontSize: 13.5 }}>{i.titel}</Typography>)}</Stack>
              </Paper>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
